import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ScrollToTop from '../../../components/ScrollToTop';

const PrivacyPolicy = () => {
    const navigator = useNavigate();
    return (
        <>
            <Helmet>
                <title>Privacy Policy | TLK</title>
            </Helmet>
            {/* start: privacy page */}
            <ScrollToTop />
            <div className="min-h-screen gradient-bg-1">
                <header className="cs-header">
                    <div className="cs-header-left">
                        <button type="button" className="cs-back-btn" onClick={() => navigator(-1)} aria-label="Go back">
                            <ArrowBackIcon fontSize="small" />
                        </button>
                        <div className="cs-title">
                            <span className="cs-bar"></span>
                            <h1>Privacy Policy</h1>
                        </div>
                    </div>
                    <div className="cs-updated"> Last updated: July 2026</div>
                </header>
                <div
                    className="p-6 pt-[100px] max-w-2xl mx-auto"
                    style={{ opacity: 1, transform: "none" }}
                >
                    <div className="bg-primary/10 border border-primary/20 rounded-2xl p-4 mb-6">
                        <div className="flex items-center gap-3 mb-2">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width={24}
                                height={24}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-shield w-6 h-6 text-primary"
                            >
                                <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                            </svg>
                            <h3 className="font-semibold ">Our Privacy Promise</h3>
                        </div>
                        <p className="text-sm ">
                            <strong>Your privacy is our priority.</strong> Your conversation
                            history belongs to you and is deletable at any time. We will
                            never sell your personal data or share it for advertising purposes.
                        </p>
                    </div>
                    <div className="space-y-6  text-sm leading-relaxed">
                        <section>
                            <h2 className="text-lg font-semibold  mb-3">
                                1. Information We Collect
                            </h2>
                            <p className="mb-2">We collect the following types of information:</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>
                                    <strong>Account Information:</strong> Email address, date of birth
                                    (for age verification)
                                </li>
                                <li>
                                    <strong>Payment Information:</strong> Processed securely through
                                    Stripe. We do not store payment card details.
                                </li>
                                <li>
                                    <strong>Usage Data:</strong> Interaction patterns, feature usage,
                                    session duration
                                </li>
                                <li>
                                    <strong>Conversation Data:</strong> Text messages exchanged within
                                    the Service, which are processed by our AI system to generate
                                    responses
                                </li>
                                <li>
                                    <strong>Voice &amp; Video Call Data:</strong> During voice and video
                                    calls, your audio is streamed in real time to Deepgram, a
                                    third-party speech recognition service, for transcription. Video
                                    call infrastructure is provided by LiveKit.
                                </li>
                                <li>
                                    <strong>Device Information:</strong> Device type, operating system,
                                    device identifiers, push notification tokens
                                </li>
                            </ul>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold  mb-3">
                                2. How We Use Your Information
                            </h2>
                            <p className="mb-2">We use your information to:</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>Provide and improve the Service</li>
                                <li>Process your text messages through our AI system to generate companion responses</li>
                                <li>Transcribe voice audio in real time using Deepgram's speech recognition service to enable voice call features</li>
                                <li>Process payments and manage subscriptions via Stripe</li>
                                <li>Verify your age and identity</li>
                                <li>Ensure security and prevent fraud</li>
                                <li>Comply with legal obligations</li>
                            </ul>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold  mb-3">
                                3. Data Ownership &amp; Your Rights
                            </h2>
                            <div className=" rounded-xl p-4 border border-border">
                                <p className="font-medium  mb-2">
                                    Your history is yours.
                                </p>
                                <p className="mb-3">
                                    All conversation data—messages, voice recordings, images—belongs to
                                    you. You have complete control over your data with the following
                                    rights:
                                </p>
                                <ul className="list-disc list-inside space-y-1 ml-2">
                                    <li>
                                        <strong>Access:</strong> View all data we hold about you
                                    </li>
                                    <li>
                                        <strong>Delete:</strong> Permanently erase your conversation
                                        history at any time
                                    </li>
                                    <li>
                                        <strong>Export:</strong> Request a copy of your data
                                    </li>
                                    <li>
                                        <strong>Account Deletion:</strong> Completely remove your account
                                        and all associated data
                                    </li>
                                </ul>
                            </div>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold  mb-3">
                                4. Data Storage &amp; Security
                            </h2>
                            <p>
                                We implement industry-standard security measures including encryption
                                in transit (TLS) and at rest to protect your information. Your data is
                                stored on secure servers with restricted access.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold  mb-3">
                                5. What We Never Do
                            </h2>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>
                                    We <strong>never</strong> sell your personal data to third parties
                                </li>
                                <li>
                                    We <strong>never</strong> share your conversation content with
                                    advertisers
                                </li>
                                <li>
                                    We <strong>never</strong> use your private messages for training AI
                                    models without your explicit consent
                                </li>
                                <li>
                                    We <strong>never</strong> expose your identity or usage to other
                                    users
                                </li>
                            </ul>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold  mb-3">
                                6. Third-Party Services
                            </h2>
                            <p className="mb-2">
                                We use the following third-party service providers to operate the
                                Service. Each provider receives only the data necessary for their
                                specific function and is contractually required to protect your data
                                to the same or equivalent standard as described in this policy.
                            </p>
                            <div className="overflow-x-auto rounded-xl border border-border">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-card/50 text-left">
                                            <th className="p-3 font-semibold ">Provider</th>
                                            <th className="p-3 font-semibold ">Purpose</th>
                                            <th className="p-3 font-semibold ">Data Received</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="border-t border-border align-top">
                                            <td className="p-3">Deepgram (deepgram.com)</td>
                                            <td className="p-3">Real-time speech-to-text AI for voice calls</td>
                                            <td className="p-3">Live voice audio during calls</td>
                                        </tr>
                                        <tr className="border-t border-border align-top">
                                            <td className="p-3">LiveKit (livekit.io)</td>
                                            <td className="p-3">Real-time audio/video call infrastructure</td>
                                            <td className="p-3">Audio and video streams during calls</td>
                                        </tr>
                                        <tr className="border-t border-border align-top">
                                            <td className="p-3">Clerk (clerk.com)</td>
                                            <td className="p-3">User authentication (sign-up, sign-in, OAuth)</td>
                                            <td className="p-3">Email, name, date of birth, OAuth tokens</td>
                                        </tr>
                                        <tr className="border-t border-border align-top">
                                            <td className="p-3">Stripe (stripe.com)</td>
                                            <td className="p-3">Subscription payment processing</td>
                                            <td className="p-3">Payment card details, billing information</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p className="mt-2">
                                These providers maintain their own privacy policies. We encourage you
                                to review them. We do not permit these providers to use your data for
                                their own marketing or advertising purposes.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold  mb-3">
                                7. AI Data Processing
                            </h2>
                            <p className="mb-2">
                                Syntimacy is an AI companion service. To deliver this service, your
                                data interacts with AI systems as follows:
                            </p>
                            <p className="font-medium  mb-1">Text Messages</p>
                            <p className="mb-3">
                                Text messages you send are transmitted to Syntimacy's AI backend,
                                which generates the companion's responses. These messages are
                                processed solely to operate the Service and are not used to train AI
                                models without your explicit consent.
                            </p>
                            <p className="font-medium  mb-1">Voice Calls</p>
                            <p className="mb-3">
                                During voice and video calls, your audio is streamed in real time to
                                Deepgram (deepgram.com), a third-party AI speech recognition service.
                                Deepgram converts your speech to text to enable voice interaction
                                features. Deepgram processes this data under their privacy policy,
                                available at deepgram.com/privacy.
                            </p>
                            <p className="font-medium  mb-1">Your Consent</p>
                            <p>
                                Before accessing voice and video features for the first time, we will
                                ask for your explicit consent to send audio data to Deepgram. You may
                                decline, in which case voice and video call features will not be
                                available. You may withdraw consent at any time by contacting
                                privacy@syntimacy.co.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold  mb-3">
                                8. Data Retention
                            </h2>
                            <p>
                                We retain your data only as long as your account is active or as
                                needed to provide the Service. When you delete your data or account,
                                it is permanently removed from our systems within 30 days, except
                                where required by law.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold  mb-3">
                                9. Age Verification
                            </h2>
                            <p>
                                We collect date of birth solely to verify you are 18 or older. This
                                information is used only for age verification and is protected with
                                the same security as all other personal data.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold  mb-3">
                                10. Cookies &amp; Tracking
                            </h2>
                            <p>
                                We use essential cookies to maintain your session and preferences. We
                                do not use third-party advertising cookies or cross-site tracking.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold mb-3">11. Our Position</h2>
                            <p>
                                TLK does not condone human trafficking, sex trafficking, forced labor, or any
                                form of exploitation. These activities are strictly prohibited on our platform
                                and in any content, account, or conduct connected to our Service.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold mb-3">12. Reporting to Authorities</h2>
                            <p>
                                All instances of suspected human trafficking will be reported to the proper
                                authorities. We will cooperate with law enforcement investigations as required
                                by applicable law.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold mb-3">13. How We Prevent Exploitation</h2>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>Creators are reviewed, contracted, and onboarded directly by TLK; there is no open self-service creator sign-up</li>
                                <li>Every user must confirm they are 18 or older before accessing the site</li>
                                <li>Content that appears to depict coerced, non-consenting, or exploited persons is removed</li>
                                <li>Accounts connected to suspected trafficking are suspended or terminated</li>
                            </ul>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold mb-3">14. Report a Concern</h2>
                            <p className="mb-2">
                                If you suspect trafficking or exploitation connected to TLK, contact us at{' '}
                                <a className="underline" href="mailto:support@tlknow.ai">support@tlknow.ai</a>{' '}
                                or use our <Link className="underline" to="/complaints-policy">Complaints &amp; Content Removal Policy</Link>.
                            </p>
                            <p>
                                If someone is in immediate danger, contact local emergency services. In the
                                United States you can also call the National Human Trafficking Hotline at
                                1-888-373-7888 or text 233733.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold  mb-3">
                                15. Changes to This Policy
                            </h2>
                            <p>
                                We may update this Privacy Policy from time to time. We will notify
                                you of significant changes via email or in-app notification.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold  mb-3">
                                16. Contact Us
                            </h2>
                            <p>
                                For privacy-related questions or to exercise your data rights, contact
                                us at privacy@syntimacy.co
                            </p>
                        </section>
                    </div>
                    <div className="mt-8 pt-6 border-t border-border">
                        <p className="text-xs text-muted-foreground text-center">
                            Copyright 2026 Syntimacy LLC. All rights reserved.
                        </p>
                    </div>
                </div>
            </div>

            {/* end: privacy page */}
        </>
    );
}

export default PrivacyPolicy;